import React, { createContext, useContext, useState, useEffect } from 'react'
import { createTheme, ThemeProvider } from '@mui/material/styles'
import CssBaseline from '@mui/material/CssBaseline'

// Theme mode options
export const THEME_MODES = {
  LIGHT: 'light',
  NIGHT: 'night',
  SYSTEM: 'system'
}

// Create theme context
const ThemeContext = createContext({
  themeMode: THEME_MODES.LIGHT,
  actualMode: THEME_MODES.LIGHT, // resolved mode (when system is selected)
  setThemeMode: () => {},
  availableThemes: Object.values(THEME_MODES)
})

// Theme configurations
const getThemeConfig = (mode) => {
  const baseTheme = {
    typography: {
      fontFamily: 'Roboto, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
      h1: { fontWeight: 500 },
      h2: { fontWeight: 500 },
      h3: { fontWeight: 500 },
      h4: { fontWeight: 500 },
      h5: { fontWeight: 500 },
      h6: { fontWeight: 500 },
    },
    shape: {
      borderRadius: 8,
    },
    components: {
      MuiCssBaseline: {
        styleOverrides: {
          // Custom scrollbar for all themes
          '*::-webkit-scrollbar': {
            width: '8px',
            height: '8px',
          },
          '*::-webkit-scrollbar-track': {
            backgroundColor: mode === 'light' ? '#f1f1f1' : '#2d2d30',
            borderRadius: '4px',
          },
          '*::-webkit-scrollbar-thumb': {
            backgroundColor: mode === 'light' ? '#c1c1c1' : '#555',
            borderRadius: '4px',
          },
          '*::-webkit-scrollbar-thumb:hover': {
            backgroundColor: mode === 'light' ? '#a8a8a8' : '#777',
          },
        },
      },
      MuiPaper: {
        styleOverrides: {
          root: ({ ownerState }) => ({
            backgroundImage: 'none',
            // Make headers darker when they're at the top level in night mode
            ...(mode === 'dark' && ownerState?.elevation === 1 && {
              backgroundColor: '#1f2328', // Darker than default for headers
            }),
          }),
        },
      },
      // Enhanced input styling for night mode
      ...(mode === 'dark' && {
        MuiTextField: {
          styleOverrides: {
            root: {
              '& .MuiOutlinedInput-root': {
                backgroundColor: '#2a2f37', // Slightly lighter than paper for differentiation
                borderColor: 'var(--agentflow-subtle-border)',
                '&:hover': {
                  backgroundColor: '#2e3339',
                  borderColor: 'var(--agentflow-subtle-border)',
                },
                '&.Mui-focused': {
                  backgroundColor: '#2e3339',
                  borderColor: 'var(--agentflow-focus-glow)',
                  boxShadow: '0 0 0 2px rgba(79, 144, 226, 0.15)',
                },
                '&.Mui-error.Mui-focused': {
                  borderColor: 'var(--agentflow-error-muted)',
                  boxShadow: '0 0 0 2px rgba(239, 83, 80, 0.15)',
                },
              },
            },
          },
        },
        MuiAppBar: {
          styleOverrides: {
            root: {
              backgroundColor: '#151821', // Darker than default background for headers
            },
          },
        },
      }),
    },
  }

  // Light theme configuration
  if (mode === 'light') {
    return createTheme({
      ...baseTheme,
      palette: {
        mode: 'light',
        primary: {
          main: '#18244c',
          light: '#4a5a7a',
          dark: '#0d1328',
          contrastText: '#ffffff',
        },
        secondary: {
          main: '#f9af43',
          light: '#fbc269',
          dark: '#f7931e',
          contrastText: '#000000',
        },
        background: {
          default: '#f5f5f5',
          paper: '#ffffff',
        },
        text: {
          primary: '#1a1a1a',
          secondary: '#6b6b6b',
        },
        success: { main: '#2e7d32' },
        warning: { main: '#ed6c02' },
        error: { main: '#d32f2f' },
        info: { main: '#0288d1' },
      },
    })
  }

  // Night theme configuration (not too dark)
  const nightTheme = createTheme({
    ...baseTheme,
    palette: {
      mode: 'dark',
      primary: {
        main: '#4a90e2',
        light: '#7bb3f0',
        dark: '#2c5aa0',
        contrastText: '#ffffff',
      },
      secondary: {
        main: '#ffc947',
        light: '#ffd369',
        dark: '#f9af43',
        contrastText: '#000000',
      },
      background: {
        default: '#1a1d23', // Soft dark, not too black
        paper: '#242830',   // Slightly lighter for cards/papers
      },
      // Add custom backgrounds for better visual hierarchy
      action: {
        hover: 'rgba(255, 255, 255, 0.08)', // Less white hover - reduced from default 0.12
        selected: 'rgba(255, 255, 255, 0.12)',
        focus: 'rgba(79, 144, 226, 0.15)', // Soft primary color focus
      },
      text: {
        primary: '#e8eaed',
        secondary: '#b3b8c4',
      },
      // Desaturated status colors for night mode accessibility
      success: { main: '#66bb6a', dark: '#4caf50' }, // Softer green
      warning: { main: '#ffb74d', dark: '#ff9800' }, // Softer orange
      error: { main: '#ef5350', dark: '#f44336' },   // Softer red
      info: { main: '#42a5f5', dark: '#29b6f6' },    // Softer blue
      // Custom night mode colors
      custom: {
        messageUser: 'rgba(74, 144, 226, 0.15)',     // Desaturated user message bg
        messageAgent: 'rgba(255, 201, 71, 0.12)',    // Desaturated agent message bg
        memoryCard: 'rgba(179, 184, 196, 0.08)',     // Subtle memory card bg
        focusGlow: 'rgba(79, 144, 226, 0.3)',        // Focus glow color
        subtleBorder: 'rgba(255, 255, 255, 0.12)',   // Subtle border replacement
        shadowElevation: '0px 2px 8px rgba(0, 0, 0, 0.25)', // Soft shadow
      },
    },
  })

  // Add CSS custom properties and global accessibility enhancements for night mode
  nightTheme.components.MuiCssBaseline.styleOverrides = {
    ...nightTheme.components.MuiCssBaseline.styleOverrides,
    ':root': {
      // Night mode CSS variables
      '--agentflow-bg-default': '#1a1d23',
      '--agentflow-bg-paper': '#242830',
      '--agentflow-text-primary': '#e8eaed',
      '--agentflow-text-secondary': '#b3b8c4',
      '--agentflow-message-user': 'rgba(74, 144, 226, 0.15)',
      '--agentflow-message-agent': 'rgba(255, 201, 71, 0.12)',
      '--agentflow-memory-card': 'rgba(179, 184, 196, 0.08)',
      '--agentflow-focus-glow': 'rgba(79, 144, 226, 0.3)',
      '--agentflow-subtle-border': 'rgba(255, 255, 255, 0.12)',
      '--agentflow-shadow-elevation': '0px 2px 8px rgba(0, 0, 0, 0.25)',
      '--agentflow-success-muted': '#66bb6a',
      '--agentflow-warning-muted': '#ffb74d',
      '--agentflow-error-muted': '#ef5350',
      '--agentflow-info-muted': '#42a5f5',
    },
    // Global accessibility enhancements for night mode
    'button:focus-visible, input:focus-visible, textarea:focus-visible, [tabindex]:focus-visible': {
      outline: '2px solid var(--agentflow-focus-glow)',
      outlineOffset: '2px',
      borderRadius: '4px',
    },
    // Enhanced scrollbar for better visibility in dark mode
    '*::-webkit-scrollbar-thumb': {
      backgroundColor: '#555',
      borderRadius: '4px',
      border: '2px solid transparent',
      backgroundClip: 'content-box',
    },
    '*::-webkit-scrollbar-thumb:hover': {
      backgroundColor: '#777',
    },
    // Reduce motion for users who prefer it
    '@media (prefers-reduced-motion: reduce)': {
      '*': {
        animationDuration: '0.01ms !important',
        animationIterationCount: '1 !important',
        transitionDuration: '0.01ms !important',
      },
    },
  }

  return nightTheme
}

// Hook to use theme context
export const useThemeMode = () => {
  const context = useContext(ThemeContext)
  if (context === undefined) {
    throw new Error('useThemeMode must be used within a ThemeContextProvider')
  }
  return context
}

// Theme provider component
export const ThemeContextProvider = ({ children }) => {
  const [themeMode, setThemeModeState] = useState(() => {
    // Initialize from localStorage or default to light
    const saved = localStorage.getItem('themeMode')
    return saved && Object.values(THEME_MODES).includes(saved) ? saved : THEME_MODES.LIGHT
  })

  const [systemPrefersDark, setSystemPrefersDark] = useState(false)

  // Detect system preference
  useEffect(() => {
    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)')
    setSystemPrefersDark(mediaQuery.matches)

    const handler = (e) => setSystemPrefersDark(e.matches)
    mediaQuery.addEventListener('change', handler)
    return () => mediaQuery.removeEventListener('change', handler)
  }, [])

  // Determine actual mode to use
  const actualMode = themeMode === THEME_MODES.SYSTEM 
    ? (systemPrefersDark ? THEME_MODES.NIGHT : THEME_MODES.LIGHT)
    : themeMode

  // Create theme based on actual mode
  const theme = getThemeConfig(actualMode)

  // Theme mode setter with persistence
  const setThemeMode = (mode) => {
    if (Object.values(THEME_MODES).includes(mode)) {
      setThemeModeState(mode)
      localStorage.setItem('themeMode', mode)
    }
  }

  const contextValue = {
    themeMode,
    actualMode,
    setThemeMode,
    availableThemes: Object.values(THEME_MODES),
    systemPrefersDark,
  }

  return (
    <ThemeContext.Provider value={contextValue}>
      <ThemeProvider theme={theme}>
        <CssBaseline />
        {children}
      </ThemeProvider>
    </ThemeContext.Provider>
  )
}

export default ThemeContextProvider